import {
  Stack,
  List,
  DialogContent,
  ListItem,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
  Alert,
  Box,
} from '@mui/joy';
import { Delete } from '@mui/icons-material';
import StaticSingleAutocomplete from 'components/technical/inputs/Autocomplete/StaticSingleAutocomplete.tsx';
import type { StaticAutocompleteOption } from 'components/technical/inputs/Autocomplete/StaticSingleAutocomplete.props';
import { useState } from 'react';

interface ColsHandlerProps {
  isDefault: boolean;
  gridColIds: string[];
  cols: string[];
  onReplace: (oldColId: string, newColId: string) => Promise<void>;
  onDelete: (colId: string) => Promise<void>;
}

export default function ColsHandler({ isDefault, cols, onReplace, onDelete, gridColIds }: ColsHandlerProps) {
  const [loadingColId, setLoadingColId] = useState<string | null>(null);

  const colOptions: StaticAutocompleteOption<string>[] = gridColIds
    .map((col: string) => ({
      label: col,
      value: col,
      key: col,
      searchText: col,
    }))
    .sort((a, b) => (a.label as string).localeCompare(b.label as string));

  return (
    <DialogContent>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        {isDefault && (
          <Alert color="warning" variant="soft" invertedColors sx={{ mt: 2, width: '100%' }}>
            <Box sx={{ flex: 1 }}>
              <Typography level="title-sm">Default preset</Typography>
              <Typography level="body-xs">
                This column is part of the default preset and can only be modified locally.
              </Typography>
            </Box>
          </Alert>
        )}
      </Stack>
      <Stack spacing={1}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" px={1} pt={1}>
          <Typography level="body-xs" fontWeight="lg" sx={{ flex: 1 }}>
            Preset column
          </Typography>
          <Typography level="body-xs" fontWeight="lg">
            Actions
          </Typography>
        </Stack>
        <List sx={{ p: 0 }}>
          {cols.map((currentColId, index) => {
            return (
              <ListItem
                key={`${currentColId}-${index}`}
                sx={{ pr: 0, borderBottom: '1px solid', borderColor: 'divider' }}
              >
                <Stack width="100%" spacing={4} direction="row" justifyContent="space-between" alignItems="center">
                  <Chip size="sm" variant="outlined" color="primary" sx={{ wordBreak: 'break-word', flex: 1 }}>
                    {currentColId}
                  </Chip>
                  <Stack direction="row" justifyContent="flex-end" alignItems="center" gap={1}>
                    <StaticSingleAutocomplete<string>
                      label="Link to an existing column"
                      width="xl4"
                      disabled={loadingColId === currentColId || isDefault}
                      value={currentColId}
                      onChange={async (newColId) => {
                        if (newColId) {
                          const oldColId = currentColId.replace(/^"(.*)"$/, '$1');
                          setLoadingColId(oldColId);
                          await onReplace(oldColId, newColId);
                          setLoadingColId(null);
                        }
                      }}
                      options={colOptions}
                    />
                    <IconButton
                      disabled={loadingColId === currentColId || isDefault}
                      onClick={async () => {
                        setLoadingColId(currentColId);
                        await onDelete(currentColId);
                        setLoadingColId(null);
                      }}
                    >
                      {loadingColId === currentColId ? <CircularProgress size="sm" /> : <Delete />}
                    </IconButton>
                  </Stack>
                </Stack>
              </ListItem>
            );
          })}
        </List>
      </Stack>
    </DialogContent>
  );
}
